import { useApiClient } from '@m43/core';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, FormRow, Box } from '@cad-arc';
import { DexEntityTypeEnumType, UnitResource } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { globalAttributes } from '../../common/global-attributes';
import { useDispatchAreaStore } from '../../dispatch-areas/use-dispatch-areas';
import { FormEventAutocomplete } from '../../forms/integrated-form-controls/form-event-autocomplete';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { FormUnitSelect } from '../../forms/integrated-form-controls/form-unit-select';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { DexFormModel, DexInquiry } from '../types';
import { useShouldShowRequestorSelect } from './use-should-show-requestor-select';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';
import { FormCheckbox } from '../../forms/integrated-form-controls/form-checkbox';
import { useIsLinkToAssignedEventCheckboxVisible } from './use-is-link-to-assigned-even-visible';

export const DexQueryRoutingSection = observer(function DexQueryRoutingSection({
    dexEntityType,
    eventNumber,
}: {
    dexEntityType: DexEntityTypeEnumType;
    eventNumber?: string;
}): React.ReactElement | null {
    const apiClient = useApiClient();
    const { form } = useIntegratedForm<DexFormModel<DexInquiry>>();
    const dispatchAreaIds = useDispatchAreaStore().monitoredAndOwnedDispatchAreaIds;
    const { dexRegion } = useBootstrapState();
    const requestingUnitOption = useWatch({ control: form.control, name: 'requestingUnitOption' });
    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();
    const isLinkToAssignedEventCheckboxVisible = useIsLinkToAssignedEventCheckboxVisible();
    const requestingUnitId = requestingUnitOption?.value;
    const showRequestorSelect = useShouldShowRequestorSelect(dexEntityType, dexRegion);
    const { data: userOptions, isInitialLoading: isUserOptionsLoading } = useQuery(
        ['requesting-unit-user-options', requestingUnitId] as const,
        ({ queryKey }) => {
            return apiClient
                .request<UnitResource.GetUnitUsersAsOptions>({
                    method: 'GET',
                    url: `unit/${queryKey[1] as number}/users/options`,
                })
                .then((response) => response.data.data);
        },
        { enabled: showRequestorSelect && !!requestingUnitId }
    );
    useEffect(() => {
        if (userOptions && userOptions.length > 0) {
            form.setValue('requestingUserOption', userOptions[0]);
        } else {
            form.setValue('requestingUserOption', null);
        }
    }, [userOptions, form]);

    if (
        isFirstResponder &&
        (!unitAssignment?.unitId || (!isLinkToAssignedEventCheckboxVisible && !showRequestorSelect))
    ) {
        return null;
    }

    return (
        <Card style={{ width: '100%' }}>
            <Box bgColor="surface.accent">
                <CardBody>
                    <FormRow>
                        {!isFirstResponder && (
                            <FormUnitSelect
                                label="Requesting Unit"
                                length="md"
                                control={form.control}
                                name="requestingUnitOption"
                                unitSelectProps={{
                                    dispatchAreaIds,
                                    showSelectedGroup: false,
                                    agencyTypeGlobalAttrIds: [
                                        globalAttributes.agencyTypeGlobal.POLICE,
                                    ],
                                }}
                            />
                        )}
                        {showRequestorSelect && (
                            <FormSelect
                                label="Requestor / Attention"
                                length="md"
                                control={form.control}
                                name="requestingUserOption"
                                options={userOptions ?? []}
                                isLoading={isUserOptionsLoading}
                                selectProps={{
                                    emptyState: requestingUnitOption
                                        ? userOptions && userOptions.length > 0
                                            ? 'No results found'
                                            : 'No unit members found'
                                        : 'Select Requesting Unit',
                                }}
                            />
                        )}
                        {!isFirstResponder && (
                            <FormEventAutocomplete
                                label="Event"
                                length="md"
                                control={form.control}
                                name="agencyEventOption"
                                eventAutocompleteProps={{
                                    agencyTypeGlobalAttrIds: [
                                        globalAttributes.agencyTypeGlobal.POLICE,
                                    ],
                                    defaultValue: null,
                                    initialInputValue: eventNumber,
                                }}
                            />
                        )}
                        {isLinkToAssignedEventCheckboxVisible && (
                            <FormCheckbox
                                name="linkToAssignedEvent"
                                label="Link to current assigned event"
                                control={form.control}
                            />
                        )}
                    </FormRow>
                </CardBody>
            </Box>
        </Card>
    );
});
